import { Button, DynamicContainer, Form, FormContext, Icon } from '@hai/ui-react';
import { ButtonStateType } from '@hai/ui-react/dist/types';
import { ApiErrorResponse, ApiOkResponse } from 'apisauce';
import { FormikProps } from 'formik';
import React, { Ref, forwardRef, useEffect, useImperativeHandle, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { FormTextfield } from 'src/js/component/base/form/form-textfield';
import { FormBuilder, FormFieldType, FormLayout } from 'src/js/component/form-builder';
import systemInfo, { systemInfoCapabilities } from 'src/js/data/systemInfo';
import { triggerEvent } from 'src/js/events';
import { useStores } from 'src/js/hook/use-stores';
import streams, {
  addStreamForm,
  fetchStreamStatistics,
  updateStreamForm,
} from 'src/js/pages/streaming/stream/stream-store';
import { useWatchObject } from 'src/js/store/use-watch';
import { submitAndValidate } from 'src/js/validator/validator';

import { StreamEditAccessControlForm, StreamEditAccessControlFormRef } from './stream-edit-access-control';
import { SourceType } from './stream-helper';
import {
  FecRTP,
  GetStreamResponse,
  NetworkInterfaces,
  SRTAuthentication,
  SRTAuthenticationListener,
  SRTAuthenticationOthers,
  SRTMode,
  SrtPathRedundancyMode,
  SrtPathRedundancyModeOffered,
  SrtRedundancyPathRole,
  StreamEncapsulation,
  StreamState,
  StreamViewModel,
  TsPidType,
  streamValidationSchema,
  supportedStreamEncapsulation,
  supportedStreamEncapsulationWithoutSRT,
} from './stream-models';
import { FormRadios } from 'src/js/component/base/form/form-radios';
import { AudioPidPairs } from './audio-pid-pairs';
import { changes } from 'src/js/util/formik-helper';
import { createAndDispatchNotification } from 'src/js/notification/notification-helper';
import { NotificationVariant } from 'src/js/notification/notification';
import { constant } from 'src/js/constant';

interface Props {
  model: StreamViewModel;
  onCancel?: VoidFunction;
}

export interface StreamAddFormRef {
  submitAddStream: () => Promise<ApiErrorResponse<GetStreamResponse> | ApiOkResponse<GetStreamResponse>>;
}

export const StreamEdit = forwardRef((props: Props, ref: Ref<StreamAddFormRef>) => {
  const { model, onCancel } = props;
  const { t } = useTranslation();
  const { sessionStore } = useStores();

  const formRef = useRef<FormikProps<StreamViewModel>>();
  const accessControlFormRef = useRef<StreamEditAccessControlFormRef>();
  const nameField = useRef<HTMLInputElement>();
  const newStream = model.id === -1;

  // setting a state here prevents live update from modifying the edit form
  const [settings, setSettings] = useState<StreamViewModel>(model);
  const [buttonState, setButtonState] = useState<ButtonStateType>(undefined);
  const anyPidsManuallySet =
    model.audioPids?.length > 0 ||
    model.videoPids?.length ||
    model.dataPids?.length ||
    model.programNumber > 0 ||
    model.downmixSurround === true
      ? true
      : false;
  const [showTsFields, setShowTsFields] = useState(anyPidsManuallySet);

  useEffect(() => {
    if (!newStream) {
      fetchStreamStatistics(model, t, setSettings); // refetch individual is required to load sap parameters
    } else {
      nameField.current?.focus?.();
    }
  }, []);

  useWatchObject(streams, (_updates) => {
    // Find the updated stream that matches our current model
    const updatedStream = _updates.find((stream) => stream.id === model.id);

    if (!updatedStream) {
      return;
    }

    // Check if either detectedPids or sources have changed
    const hasDetectedPidsChanged =
      JSON.stringify(updatedStream.stats?.detectedPids) !== JSON.stringify(settings.stats?.detectedPids);

    const hasSourcesChanged = JSON.stringify(updatedStream.stats?.sources) !== JSON.stringify(settings.stats?.sources);
    const hasStateChanged = updatedStream.stats?.state !== settings.stats?.state;
    const hasVideoPidsChangbed = updatedStream.videoPidsPlaceholder !== settings.videoPidsPlaceholder;
    const hasAutoPidChanged = updatedStream.dataPidsPlaceholder !== settings.dataPidsPlaceholder;
    const hasProgramNumberChanged = updatedStream.programNumberPlaceholder !== settings.programNumberPlaceholder;
    const hasPcrPidChanged = updatedStream.stats.pcrPid !== settings.stats.pcrPid;
    const hasPmtPidChanged = updatedStream.stats.pmtPid !== settings.stats.pmtPid;

    // Only update if either has changed
    if (
      hasDetectedPidsChanged ||
      hasSourcesChanged ||
      hasStateChanged ||
      hasVideoPidsChangbed ||
      hasAutoPidChanged ||
      hasProgramNumberChanged ||
      hasPcrPidChanged ||
      hasPmtPidChanged
    ) {
      // Create a new settings object with just the updated properties
      const updatedSettings = {
        ...settings,
        dataPidsPlaceholder: updatedStream.dataPidsPlaceholder,
        programNumberPlaceholder: updatedStream.programNumberPlaceholder,
        videoPidsPlaceholder: updatedStream.videoPidsPlaceholder,
        stats: {
          ...settings.stats,
          pcrPid: updatedStream.stats.pcrPid as any,
          pmtPid: updatedStream.stats.pmtPid as any,
          state: updatedStream.stats?.state,
          detectedPids: updatedStream.stats?.detectedPids || [],
          sources: updatedStream.stats?.sources || [],
        },
      };

      // Update the state
      setSettings(updatedSettings);

      // Update the form values without triggering validation
      if (formRef.current) {
        formRef.current.setValues(
          {
            ...formRef.current.values,
            stats: {
              ...formRef.current.values.stats,
              state: updatedStream.stats?.state,
              detectedPids: updatedStream.stats?.detectedPids || [],
              sources: updatedStream.stats?.sources || [],
            },
          },
          false,
        );
      }
    }
  });

  const handleSubmit = (values: any) => {
    setButtonState('pending');

    if (newStream) {
      return addStreamForm(values, formRef.current, t).then((res) => {
        setButtonState(undefined);
        if (res.ok) {
          triggerEvent('preset-update');
        }
        return res;
      });
    } else {
      return updateStreamForm(values, formRef.current, t).then((res: any) => {
        if (res.ok) {
          const stream = streams.find((e) => e.id === model.id);
          setSettings(stream); // update edit view with newest model
          formRef.current.resetForm({ values: stream });
          triggerEvent('preset-update');
        }
        setButtonState(undefined);
        return res;
      });
    }
  };

  // validateAudioPids()
  // Helper function used during validation on form submit (add & edit)
  // Return value: Boolean. If returning true means caller has to abort form submission.
  // tag AUDIO_PIDS_A
  const validateAudioPids = (formContext: FormikProps<StreamViewModel>) => {
    // Check for missing audio PIDs in manual mode
    const { values } = formContext;
    if (showTsFields && values.pidsAutoDetectMode === 'manual') {
      // Check if there are any audio PIDs set
      const hasAudioPids = values.pids?.some(
        (pid) => pid.type === TsPidType.AUDIO && (pid.pid as any) !== '' && pid.pid !== 0,
      );

      if (!hasAudioPids) {
        createAndDispatchNotification(t('validation.oneAudioPidRequired'), NotificationVariant.ERROR, t);
        // Ensure the TS fields section is expanded if there are errors
        if (!showTsFields) {
          setShowTsFields(true);
        }
        return true;
      }
    }
    return false;
  };

  const validateForm = (formContext: FormikProps<StreamViewModel>) => {
    // Check for missing audio PIDs in manual mode
    const { values, errors } = formContext;
    const anyManualPidValudationError =
      values.pids?.some((_, index) => {
        const fieldName = `audioPids_${index}`;
        return Object.keys(errors)?.some((key) => key === fieldName);
      }) || Object.keys(errors)?.some((key) => key.startsWith('audioPids_'));

    const abort = validateAudioPids(formContext);
    if (abort) {
      return;
    }

    submitAndValidate(formContext, t).then((_res) => {
      if (!formContext.isValid && anyManualPidValudationError && !showTsFields) {
        // If there are validation errors in manual audio PIDs and the TS fields are not shown, expand them
        setShowTsFields(true);
      }
    });
  };

  const submitAddStream = () => {
    const abort = validateAudioPids(formRef.current);
    if (abort) {
      const promise = new Promise((resolve, _reject) => {
        resolve('Audio PID validation failed');
        return;
      });
      return promise as any;
    }

    return submitAndValidate(formRef.current, t).then((res) => {
      if (!formRef.current.isValid) {
        //checkTsFields(formRef.current);
      }
      return res;
    });
  };

  useImperativeHandle(ref, () => ({
    submitAddStream,
  }));

  const onChangeSrtMode = (e: any) => {
    if (e.target.value === SRTMode.RENDEZVOUS) {
      formRef.current.setFieldValue('sourcePort', formRef.current.values.port);
    }
    if (accessControlFormRef?.current) {
      accessControlFormRef.current.onChangeSrtMode?.();
    }
  };

  const onChangePort = (e: any) => {
    if (formRef.current.values.srtMode === SRTMode.RENDEZVOUS) {
      formRef.current.setFieldValue('sourcePort', e.target.value);
    }
  };

  const isSrtRedundancyOff = (model: StreamViewModel) => {
    if (model.encapsulation !== StreamEncapsulation.SRT) {
      return true;
    }
    if (model.srtMode === SRTMode.LISTENER || model.srtMode === SRTMode.RENDEZVOUS) {
      return true;
    }
    if (
      model.srtRedundancyMode === SrtPathRedundancyMode.ACTIVE_ACTIVE ||
      model.srtRedundancyMode === SrtPathRedundancyMode.ACTIVE_BACKUP
    ) {
      return false;
    }
    return true;
  };

  return (
    <Form
      initialValues={settings}
      defaultValidation={true}
      handleSubmit={handleSubmit}
      className="hai-mt-n8"
      restValidationProps={{
        enableReinitialize: false, // "false" requires manual resetForm() above. We can then do partial update of the form while it's opened.
        validationSchema: streamValidationSchema(t),
        innerRef: formRef,
      }}
    >
      <FormContext.Consumer>
        {(genericContext) => {
          const formContext: FormikProps<StreamViewModel> = genericContext as any;
          const values = formContext.values;
          const now = performance.now();
          const appliedTime = now - values.updated;
          const hideDetectedTableBecauseOfState = values.stats.state !== StreamState.ACTIVE && !(appliedTime < 5500);
          const showRedundancy = !isSrtRedundancyOff(values);
          let showRedundancyPrimaryPathRows = false;
          if (showRedundancy) {
            if (values.srtRedundancyMode === SrtPathRedundancyMode.ACTIVE_ACTIVE) {
              showRedundancyPrimaryPathRows = false;
            } else {
              showRedundancyPrimaryPathRows = true;
            }
          }

          let showNonRedundantCallerRendezvous = false;
          if (values.encapsulation === StreamEncapsulation.SRT && showRedundancy === false) {
            if (values.srtMode === SRTMode.CALLER || values.srtMode === SRTMode.RENDEZVOUS) {
              showNonRedundantCallerRendezvous = true;
            }
          }
          const isListener = values.encapsulation === StreamEncapsulation.SRT && values.srtMode === SRTMode.LISTENER;

          const fixSrtFields = (
            srtMode: SRTMode,
            srtRedundancyMode: SrtPathRedundancyMode,
            authentication: SRTAuthentication,
          ) => {
            if (srtMode == SRTMode.LISTENER) {
              if (authentication === undefined || authentication === SRTAuthentication.NONE) {
                formContext.setFieldValue('authentication', SRTAuthenticationListener.AUTO);
              }
              if (srtRedundancyMode === undefined || srtRedundancyMode === SrtPathRedundancyMode.NONE) {
                formContext.setFieldValue('srtRedundancyMode', SrtPathRedundancyMode.OPTIONAL);
              }
            } else {
              // non-listener
              if (authentication === undefined || authentication === SRTAuthentication.AUTO) {
                formContext.setFieldValue('authentication', SRTAuthenticationOthers.NONE);
              }
              if (srtRedundancyMode === undefined || srtRedundancyMode === SrtPathRedundancyMode.OPTIONAL) {
                formContext.setFieldValue('srtRedundancyMode', SrtPathRedundancyMode.NONE);
              }
            }
          };

          let applyButtonEnabled = formContext.dirty;
          if (applyButtonEnabled) {
            const changed = changes(values, formContext.initialValues);
            if (changed.stats !== undefined) {
              delete changed.stats;
            }
            if (changed.pids !== undefined) {
              delete changed.pids;
            }

            // Filter out unchanged audioPid_ fields
            Object.keys(changed).forEach((key) => {
              if (key.startsWith('audioPids_')) {
                const initialValue = (formContext.initialValues as Record<string, any>)[key];
                const currentValue = changed[key];

                // Check if values are the same, treating '' and 0 as equivalent
                if (
                  currentValue === initialValue ||
                  (currentValue === '' && initialValue === 0) ||
                  (currentValue === 0 && initialValue === '')
                ) {
                  delete changed[key];
                }
              }
            });

            if (values.srtRedundancyMode === SrtPathRedundancyMode.NONE) {
              if (changed.srtRedundancyPath2Port !== undefined) {
                delete changed.srtRedundancyPath2Port; // not relevent without redundancy
              }
              if (changed.srtRedundancyPath2Address !== undefined) {
                delete changed.srtRedundancyPath2Address; // not relevent without redundancy
              }
              if (changed.srtRedundancyPath2SourcePort !== undefined) {
                delete changed.srtRedundancyPath2SourcePort; // not relevent without redundancy
              }
              if (changed.srtRedundancyPath2Name !== undefined) {
                delete changed.srtRedundancyPath2Name; // not relevent without redundancy
              }
              if (changed.srtRedundancyPath2NetworkInterface !== undefined) {
                delete changed.srtRedundancyPath2NetworkInterface; // not relevent without redundancy
              }
            }
            // final calculation
            //console.log('changed', changed, formContext.initialValues);
            if (Object.keys(changed).length === 0) {
              applyButtonEnabled = false;
            }
          }
          // console.log('values', values)
          return (
            <>
              <DynamicContainer minColumns={2} maxColumns={2} className="mk-mt-panel-name-align">
                <FormTextfield
                  inputRef={nameField}
                  name="name"
                  mask
                  placeholder={values.name}
                  title={t('general.name')}
                  className="max-width"
                  autoFocus={newStream}
                />
                {!newStream && !sessionStore.isUser() && (
                  <div className="d-flex ml-auto hai-mt-panel-apply-sticky">
                    <Button className="hai-mr-6" onClick={onCancel}>
                      {t('general.cancel')}
                    </Button>
                    <Button
                      variant="primary"
                      onClick={() => validateForm(formContext)}
                      disabled={!applyButtonEnabled}
                      state={buttonState}
                    >
                      {t('general.apply')}
                    </Button>
                  </div>
                )}
              </DynamicContainer>

              <FormBuilder
                translationPrefixLabels="stream.fields"
                translationPrefixEnums="stream.enums"
                layout={FormLayout.THREE_COLUMNS_GRID}
                fields={[
                  {
                    type: FormFieldType.TITLE,
                    label: t('stream.sections.parameters'),
                  },
                  {
                    type: FormFieldType.DROPDOWN,
                    name: 'encapsulation',
                    enum: systemInfoCapabilities.hasSRT()
                      ? supportedStreamEncapsulation
                      : supportedStreamEncapsulationWithoutSRT,
                  },
                  {
                    type: FormFieldType.DROPDOWN,
                    name: 'srtMode',
                    enum: SRTMode,
                    when: values.encapsulation === StreamEncapsulation.SRT,
                    onChange: (value: any) => {
                      fixSrtFields(value.target.value, values.srtRedundancyMode, values.authentication);
                      onChangeSrtMode(value);
                    },
                  },
                  {
                    type: FormFieldType.DROPDOWN,
                    name: 'srtRedundancyMode',
                    options: Object.values(SrtPathRedundancyModeOffered).map((value) => ({
                      value: value,
                      label: t(`stream.enums.srtRedundancyOptions.${value}`),
                    })),
                    value: values.srtRedundancyMode,
                    when: values.encapsulation === StreamEncapsulation.SRT && values.srtMode === SRTMode.CALLER,
                  },
                  {
                    type: FormFieldType.DROPDOWN,
                    name: 'sourceType',
                    enum: SourceType,
                    when:
                      values.encapsulation === StreamEncapsulation.UDP ||
                      values.encapsulation === StreamEncapsulation.RTP,
                  },
                  {
                    breakBefore: true,
                    type: FormFieldType.INPUT_NUMBER,
                    name: 'port',
                    mask: true,
                    hint: t('stream.fields.portExplain'),
                    when:
                      isSrtRedundancyOff(values) &&
                      values.encapsulation === StreamEncapsulation.SRT &&
                      values.srtMode === SRTMode.LISTENER,
                    required: true,
                  },
                  {
                    type: FormFieldType.INPUT_NUMBER,
                    name: 'srtListenerSecondPort',
                    mask: true,
                    placeholder: t('stream.fields.srtListenerSecondPortPlaceholder'),
                    when: values.encapsulation === StreamEncapsulation.SRT && values.srtMode === SRTMode.LISTENER,
                  },
                  {
                    type: FormFieldType.INPUT_TEXT,
                    name: 'address',
                    mask: true,
                    when:
                      isSrtRedundancyOff(values) &&
                      values.encapsulation === StreamEncapsulation.SRT &&
                      (showRedundancy || !isListener),
                    required: true,
                  },
                  /* {
                    type: FormFieldType.LINE_BREAK,
                    when: !isSrtRedundancyOff(values.srtRedundancyMode)
                  },*/
                  {
                    type: FormFieldType.INPUT_NUMBER,
                    name: 'sourcePort',
                    mask: true,
                    placeholder: t('stream.fields.srtRedundancyPath1SourcePortPlaceholder'),
                    hint: t('stream.fields.portExplain'),
                    when: !showRedundancy && showNonRedundantCallerRendezvous,
                    disabled: values.srtMode === SRTMode.RENDEZVOUS,
                  },
                  {
                    type: FormFieldType.INPUT_NUMBER,
                    name: 'port',
                    mask: true,
                    label: t('stream.fields.srtRedundancyPath1Port'),
                    hint: t('stream.fields.portExplain'),
                    when: !showRedundancy && showNonRedundantCallerRendezvous,
                    required: true,
                    onChange: onChangePort,
                  },
                  {
                    type: FormFieldType.INPUT_NUMBER,
                    name: 'port',
                    mask: true,
                    hint:
                      values.encapsulation === StreamEncapsulation.RTP
                        ? t('stream.fields.portNumberRTPExplain')
                        : t('stream.fields.portExplain'),
                    required: true,
                    when:
                      values.encapsulation === StreamEncapsulation.UDP ||
                      values.encapsulation === StreamEncapsulation.RTP,
                  },
                  {
                    type: FormFieldType.INPUT_TEXT,
                    name: 'address',
                    label: t('stream.fields.multicastAddress'),
                    mask: true,
                    when:
                      (values.encapsulation === StreamEncapsulation.UDP ||
                        values.encapsulation === StreamEncapsulation.RTP) &&
                      values.sourceType === SourceType.multicast,
                    required: values.encapsulation !== StreamEncapsulation.SRT,
                  },
                  {
                    type: FormFieldType.INPUT_TEXT,
                    name: 'sourceIp',
                    placeholder: t('stream.fields.sourceIpPlaceholder'),
                    mask: true,
                    when:
                      (values.encapsulation === StreamEncapsulation.UDP ||
                        values.encapsulation === StreamEncapsulation.RTP) &&
                      values.sourceType === SourceType.multicast,
                  },
                  {
                    type: FormFieldType.DROPDOWN,
                    name: 'fecRtp',
                    enum: FecRTP,
                    when: values.encapsulation === StreamEncapsulation.RTP,
                  },
                  {
                    type: FormFieldType.DROPDOWN,
                    name: 'networkInterface',
                    label: t('stream.fields.networkInterface'),
                    enum: NetworkInterfaces,
                    when:
                      systemInfo.hasEth1 &&
                      (values.encapsulation !== StreamEncapsulation.SRT ||
                        values.srtMode === SRTMode.RENDEZVOUS ||
                        (!showRedundancy && values.srtMode !== SRTMode.LISTENER)),
                  },
                  {
                    type: FormFieldType.LINE_BREAK,
                  },
                  {
                    type: FormFieldType.SWITCH,
                    label: t('stream.showTsFields'),
                    className: 'mk-inline-toggler',
                    value: showTsFields,
                    onChange: (checked: boolean) => {
                      if (!checked) {
                        // reset fields when collapsing the TS pids section
                        // tag AUDIO_PIDS_A
                        for (let i = 0; i < 8; i++) {
                          formContext.setFieldValue(
                            `audioPids_${i}`,
                            (formContext.initialValues as any)[`audioPids_${i}`],
                          ); // R4KD-2428
                        }
                        formContext.setFieldValue('downmixSurround', formContext.initialValues.downmixSurround);
                        formContext.setFieldValue('pidsAutoDetectMode', formContext.initialValues.pidsAutoDetectMode);
                        formContext.setFieldValue('audioPids', formContext.initialValues.audioPids);
                        formContext.setFieldValue('videoPids', formContext.initialValues.videoPids);
                        formContext.setFieldValue('dataPids', formContext.initialValues.dataPids);
                        formContext.setFieldValue('pids', formContext.initialValues.pids);
                        formContext.setFieldValue('programNumber', formContext.initialValues.programNumber);
                      }
                      formContext.validateForm().then(() => {
                        setShowTsFields(checked);
                      });
                    },
                  },
                  {
                    type: FormFieldType.LINE_BREAK,
                  },
                  { type: FormFieldType.SECTION_START, when: showTsFields },
                  {
                    type: FormFieldType.INPUT_TEXT,
                    name: 'videoPids',
                    placeholder:
                      settings.videoPidsPlaceholder && settings.videoPidsPlaceholder !== ''
                        ? settings.videoPidsPlaceholder
                        : t('stream.pidAutoDetect'),
                    hint: t('stream.fields.videoPidsExplain'),
                  },
                  {
                    type: FormFieldType.INPUT_TEXT,
                    name: 'dataPids',
                    placeholder:
                      settings.dataPidsPlaceholder && settings.dataPidsPlaceholder !== ''
                        ? settings.dataPidsPlaceholder
                        : t('stream.pidAutoDetect'),
                    hint: t('stream.fields.dataPidsExplain'),
                  },
                  {
                    type: FormFieldType.STATIC_TEXT,
                    name: 'pcrPid',
                    hint: t('stream.fields.pcrPidExplain'),
                    value: settings.stats.pcrPid,
                  },
                  {
                    type: FormFieldType.STATIC_TEXT,
                    name: 'pmtPid',
                    hint: t('stream.fields.pmtPidExplain'),
                    value: settings.stats.pmtPid,
                  },
                  /* TS ID not in MX4D
                  {
                    type: FormFieldType.INPUT_NUMBER,
                    name: 'tsStreamId',
                    hint: t('stream.fields.tsStreamIdExplain'),
                  },*/
                  {
                    type: FormFieldType.INPUT_NUMBER,
                    name: 'programNumber',
                    placeholder: settings.programNumberPlaceholder ?? t('stream.pidAutoDetect'),
                    hint: t('stream.fields.programNumberExplain'),
                    value: values.programNumber === 0 ? '' : values.programNumber,
                  },
                  // Audio PID Handling BEGINs
                  {
                    type: FormFieldType.LINE_BREAK,
                  },
                  {
                    type: FormFieldType.NODE,
                    node: (
                      <>
                        <div className="mk-horizontal-line mk-break-before" />
                      </>
                    ),
                  },
                  {
                    type: FormFieldType.SUBTITLE,
                    label: t('stream.sections.audioPassthrough'),
                    id: 'audio-passthrough-title',
                  },
                  {
                    type: FormFieldType.NODE,
                    node: (
                      <>
                        <FormRadios
                          name="pidsAutoDetectMode"
                          className="mt-4"
                          label={''}
                          data-auto="pids-auto-detect-mode"
                          radios={[
                            { value: 'autodetect', label: t('stream.fields.autodetect') },
                            { value: 'manual', label: t('stream.fields.manual') },
                          ]}
                        />
                      </>
                    ),
                  } /*
                  {
                    type: FormFieldType.DROPDOWN,
                    name: 'defaultAudioHandling',
                    enum: DefaultAudioHandling,
                    breakBefore: true,
                  },*/,
                  {
                    type: FormFieldType.CHECKBOX,
                    name: 'downmixSurround',
                    //   title: t('stream.fields.downmixSurroundTitle'),
                  },
                  {
                    type: FormFieldType.LINE_BREAK,
                  },
                  {
                    type: FormFieldType.NODE,
                    node: (
                      <>
                        <div className="warning-info-container mk-break-before mk-audio-passthrough-infobox grid-column-span3">
                          <Icon iconname="Information" className="hai-mr-4" color="#9595a1" />
                          {t('stream.noticeAudioPassthrough')}
                        </div>
                      </>
                    ),
                    when:
                      (hideDetectedTableBecauseOfState && !newStream) ||
                      values.stats?.detectedPids?.filter((s) => s.type === TsPidType.AUDIO).length === 0,
                  },
                  {
                    type: FormFieldType.NODE,
                    node: <AudioPidPairs {...formContext} />,
                  },
                  // Audio PID Handling ENDs
                  { type: FormFieldType.SECTION_END },
                  /* REDUNDANCY SECTION BEGINS */
                  {
                    type: FormFieldType.TITLE,
                    label: t('stream.sections.srtPathRedundancy'),
                    when:
                      showRedundancy &&
                      systemInfoCapabilities.hasSrtRedundancy() &&
                      values.encapsulation === StreamEncapsulation.SRT,
                  },
                  {
                    type: FormFieldType.SUBTITLE,
                    label: t('stream.sections.path1'),
                    when: showRedundancy,
                  },
                  {
                    type: FormFieldType.DROPDOWN,
                    name: 'srtRedundancyPath1Role',
                    enum: SrtRedundancyPathRole,
                    when: showRedundancyPrimaryPathRows,
                  },
                  {
                    type: FormFieldType.INPUT_TEXT,
                    name: 'srtRedundancyPath1Name',
                    hint: t('stream.fields.srtRedundancyPath1NameExplain'),
                    when: showRedundancy,
                  },
                  {
                    type: FormFieldType.DROPDOWN,
                    name: 'networkInterface',
                    label: t('stream.fields.networkInterface'),
                    enum: NetworkInterfaces,
                    when: systemInfo.hasEth1 && values.encapsulation === StreamEncapsulation.SRT && !isListener,
                  },
                  {
                    type: FormFieldType.INPUT_TEXT,
                    name: 'address',
                    mask: true,
                    when: values.encapsulation === StreamEncapsulation.SRT && showRedundancy && !isListener,
                    required: values.srtMode === SRTMode.CALLER,
                  },
                  {
                    type: FormFieldType.INPUT_NUMBER,
                    name: 'srtRedundancyPath1SourcePort',
                    mask: true,
                    placeholder: t('stream.fields.srtRedundancyPath1SourcePortPlaceholder'),
                    hint: t('stream.fields.portExplain'),
                    when: showRedundancy,
                    disabled: values.srtMode === SRTMode.RENDEZVOUS,
                  },
                  {
                    type: FormFieldType.INPUT_NUMBER,
                    name: 'port',
                    mask: true,
                    label: t('stream.fields.srtRedundancyPath1Port'),
                    hint: t('stream.fields.portExplain'),
                    when: showRedundancy,
                    required: true,
                    onChange: onChangePort,
                  },
                  {
                    type: FormFieldType.SUBTITLE,
                    label: t('stream.sections.path2'),
                    when:
                      values.encapsulation === StreamEncapsulation.SRT &&
                      values.srtMode === SRTMode.CALLER &&
                      (values.srtRedundancyMode === SrtPathRedundancyMode.ACTIVE_ACTIVE ||
                        values.srtRedundancyMode === SrtPathRedundancyMode.ACTIVE_BACKUP),
                  },
                  {
                    type: FormFieldType.STATIC_TEXT,
                    name: 'pathRole',
                    label: t('stream.fields.pathRole'),
                    value:
                      values.srtRedundancyPath1Role === 'secondary'
                        ? t('stream.enums.srtRedundancyRoleOptions.primary')
                        : t('stream.enums.srtRedundancyRoleOptions.secondary'),
                    when: showRedundancyPrimaryPathRows,
                  },
                  {
                    type: FormFieldType.INPUT_TEXT,
                    name: 'srtRedundancyPath2Name',
                    hint: t('stream.fields.srtRedundancyPath2NameExplain'),
                    when: showRedundancy,
                  },
                  {
                    type: FormFieldType.DROPDOWN,
                    name: 'srtRedundancyPath2NetworkInterface',
                    label: t('stream.fields.networkInterface'),
                    enum: NetworkInterfaces,
                    when: showRedundancy && systemInfo.hasEth1,
                  },
                  {
                    type: FormFieldType.INPUT_TEXT,
                    name: 'srtRedundancyPath2Address',
                    mask: true,
                    when: showRedundancy,
                    required: true,
                  },
                  {
                    type: FormFieldType.INPUT_NUMBER,
                    name: 'srtRedundancyPath2SourcePort',
                    mask: true,
                    placeholder: t('stream.fields.srtRedundancyPath2SourcePortPlaceholder'),
                    hint: t('stream.fields.portExplain'),
                    when: showRedundancy,
                  },
                  {
                    type: FormFieldType.INPUT_NUMBER,
                    name: 'srtRedundancyPath2Port',
                    mask: true,
                    hint: t('stream.fields.portExplain'),
                    when: showRedundancy,
                    required: true,
                  } /* TODO: conditional line break that supports "when" prop
                  {
                    type: FormFieldType.LINE_BREAK,
                    when: showRedundancy,
                  },*/,
                  {
                    type: FormFieldType.TITLE,
                    label: t('stream.sections.srtSettings'),
                    when: values.encapsulation === StreamEncapsulation.SRT,
                  },
                  {
                    type: FormFieldType.INPUT_NUMBER,
                    name: 'latency',
                    hint: t('stream.fields.latencyExplain'),
                    when: values.encapsulation === StreamEncapsulation.SRT,
                    required: true,
                  },
                  {
                    type: FormFieldType.SWITCH,
                    name: 'encrypted',
                    title: t('stream.fields.encryption'),
                    label: values.encrypted ? t('stream.fields.encrypted') : t('stream.fields.unencrypted'),
                    when: values.encapsulation === StreamEncapsulation.SRT,
                    onChange: () => {
                      fixSrtFields(values.srtMode, values.srtRedundancyMode, values.authentication);
                    },
                  },
                  {
                    type: FormFieldType.INPUT_PASSWORD,
                    name: 'passphrase',
                    id: 'passphrase',
                    pwdToggle: sessionStore.isUser() ? false : true,
                    autoComplete: 'new-password',
                    placeholder: values.encrypted && values.passphraseSet ? constant.obfuscatedPassphrase : undefined,
                    when: values.encapsulation === StreamEncapsulation.SRT && values.encrypted,
                    required: true,
                  },
                  {
                    type: FormFieldType.DROPDOWN,
                    name: 'authentication',
                    enum: SRTAuthenticationListener,
                    value: values.authentication /* WORKAROUND HAIUI ISSUE */,
                    when:
                      values.encapsulation === StreamEncapsulation.SRT &&
                      values.encrypted &&
                      values.srtMode === SRTMode.LISTENER,
                  },
                  {
                    type: FormFieldType.DROPDOWN,
                    name: 'authentication',
                    enum: SRTAuthenticationOthers,
                    value: values.authentication /* WORKAROUND HAIUI ISSUE */,
                    when:
                      values.encapsulation === StreamEncapsulation.SRT &&
                      values.encrypted &&
                      values.srtMode !== SRTMode.LISTENER,
                  },
                  {
                    type: FormFieldType.TITLE,
                    label: t('stream.sections.srtAccessControl'),
                    when: values.encapsulation === StreamEncapsulation.SRT && values.srtMode !== SRTMode.RENDEZVOUS,
                  },
                  {
                    type: FormFieldType.NODE,
                    node: <StreamEditAccessControlForm ref={accessControlFormRef} {...formContext} />,
                  },
                  {
                    type: FormFieldType.LINE_BREAK,
                  },
                  {
                    type: FormFieldType.TITLE,
                    label: t('stream.sections.streamConversion'),
                    className: 'hai-mb-0',
                    when: values.encapsulation === StreamEncapsulation.SRT,
                  },
                  {
                    type: FormFieldType.SWITCH,
                    name: 'udpStreamConversion',
                    label: t('stream.fields.udpStreamConversion'),
                  },
                  { type: FormFieldType.LINE_BREAK },
                  {
                    type: FormFieldType.INPUT_TEXT,
                    name: 'srtToUdpAddress',
                    mask: true,
                    when: values.udpStreamConversion,
                    required: true,
                  },
                  {
                    type: FormFieldType.INPUT_NUMBER,
                    name: 'srtToUdpPort',
                    placeholder: values.port?.toString(),
                    mask: true,
                    when: values.udpStreamConversion,
                  },
                  {
                    type: FormFieldType.INPUT_TEXT,
                    name: 'srtToUdpTos',
                    placeholder: '0x80',
                    when: values.udpStreamConversion,
                    hint: t('stream.fields.srtToUdpTosExplain'),
                  },
                  {
                    type: FormFieldType.INPUT_NUMBER,
                    name: 'srtToUdpTtl',
                    placeholder: '64',
                    when: values.udpStreamConversion,
                    hint: t('stream.fields.srtToUdpTtlExplain'),
                  },
                ]}
              />
            </>
          );
        }}
      </FormContext.Consumer>
    </Form>
  );
});

StreamEdit.displayName = 'StreamEdit';
